import { supabase, type Slot } from "../supabase"

export interface ManualBlockingHistory {
  id: string
  date: string
  time_start: string
  time_end: string
  status: string
  block_reason: string | null
  booked_by_user_id: string | null
}

export class SlotService {
  // Get available slots for a specific date (excludes slots with active bookings)
  static async getAvailableSlots(date: string): Promise<Slot[]> {
    try {
      // First, get all slots for the date
      const { data: allSlots, error: slotsError } = await supabase
        .from("slot")
        .select("*")
        .eq("date", date)
        .neq("status", "blocked")
        .order("time_start")

      if (slotsError) {
        console.error("Error getting slots:", slotsError)
        return []
      }

      if (!allSlots || allSlots.length === 0) {
        return []
      }

      // Get all active bookings for these slots
      const slotIds = allSlots.map(slot => slot.id)
      const { data: activeBookings, error: bookingsError } = await supabase
        .from("bookings")
        .select("slot_id")
        .in("slot_id", slotIds)
        .in("status", ["pending", "approved", "booked", "paid"])

      if (bookingsError) {
        console.error("Error getting active bookings:", bookingsError)
        // If we can't check bookings, fall back to checking slot status
        return allSlots.filter(slot => 
          slot.status !== "approved" &&
          slot.status !== "blocked"
        )
      }

      // Create a set of slot IDs that have active bookings
      const bookedSlotIds = new Set(
        (activeBookings || []).map(booking => booking.slot_id)
      )

      // Filter out slots that:
      // 1. Have active bookings in the bookings table (this is the primary check)
      // 2. Have status "approved" (means booked, even if booking record is missing)
      // Note: We DON'T filter by booked_by_user_id alone because it might be orphaned data
      // (booking deleted but slot still has booked_by_user_id set)
      const availableSlots = allSlots.filter(slot => {
        // Exclude if slot has an active booking in the bookings table
        if (bookedSlotIds.has(slot.id)) {
          return false
        }
        // Exclude if status is approved (means booked)
        if (slot.status === "approved") {
          return false
        }
        // Slot is available if it doesn't have an active booking
        // Even if booked_by_user_id is set but no booking exists, it's available
        return true
      })

      return availableSlots
    } catch (error) {
      console.error("Error in getAvailableSlots:", error)
      return []
    }
  }

  // Admin book slots - can book any slot including blocked ones with block reason
  static async adminBookSlots(slotIds: string[], userId: string, blockReason: string = "Admin Booking"): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("slot")
        .update({
          booked_by_user_id: userId,
          status: "approved",
          block_reason: blockReason,
        })
        .in("id", slotIds)

      if (error) {
        console.error("Error in admin booking slots:", error)
        return false
      }

      return true
    } catch (error) {
      console.error("Error in adminBookSlots:", error)
      return false
    }
  }

  // Get all slots for a specific date (including booked ones) - FIXED TO REMOVE DUPLICATES
  static async getAllSlotsForDate(date: string): Promise<Slot[]> {
    try {
      const { data, error } = await supabase
        .from("slot")
        .select("*")
        .eq("date", date)
        .order("time_start")

      if (error) {
        console.error("Error getting slots for date:", error)
        return []
      }

      // Type guard to ensure data is an array of Slot objects
      if (!data || !Array.isArray(data)) {
        return []
      }

      // Remove duplicates based on time_start and time_end
      const uniqueSlots = (data as Slot[]).reduce((acc: Slot[], current: Slot) => {
        const existingSlot = acc.find(slot => 
          slot.time_start === current.time_start && 
          slot.time_end === current.time_end
        )
        
        if (!existingSlot) {
          acc.push(current)
        } else {
          // If duplicate found, keep the one with booking or higher priority status
          if (current.booked_by_user_id || 
              (current.status === "blocked" && existingSlot.status !== "blocked") ||
              (current.status === "approved" && existingSlot.status === "pending")) {
            // Replace the existing slot with current one
            const index = acc.findIndex(slot => 
              slot.time_start === current.time_start && 
              slot.time_end === current.time_end
            )
            if (index !== -1) {
              acc[index] = current
            }
          }
        }
        
        return acc
      }, [])

      return uniqueSlots
    } catch (error) {
      console.error("Error in getAllSlotsForDate:", error)
      return []
    }
  }

  // ENHANCED: Clean up duplicate slots for a date before creating new ones
  static async cleanupDuplicateSlots(date: string): Promise<boolean> {
    try {
      const { data: allSlots, error: fetchError } = await supabase
        .from("slot")
        .select("*")
        .eq("date", date)
        .order("time_start")

      if (fetchError) {
        console.error("Error fetching slots for cleanup:", fetchError)
        return false
      }

      // Type guard to ensure allSlots is an array of Slot objects
      if (!allSlots || !Array.isArray(allSlots) || allSlots.length === 0) {
        return true
      }

      // Group slots by time_start and time_end
      const slotGroups = (allSlots as Slot[]).reduce((acc, slot) => {
        const key = `${slot.time_start}-${slot.time_end}`
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(slot)
        return acc
      }, {} as Record<string, Slot[]>)

      // For each group with duplicates, keep the best one and delete others
      for (const [timeKey, slotArray] of Object.entries(slotGroups)) {
        if (slotArray.length > 1) {
          // Sort by priority: booked > blocked > approved > pending
          const sortedSlots = [...slotArray].sort((a, b) => {
            const getPriority = (slot: Slot) => {
              if (slot.booked_by_user_id) return 4
              if (slot.status === "blocked") return 3
              if (slot.status === "approved") return 2
              if (slot.status === "pending") return 1
              return 0
            }
            return getPriority(b) - getPriority(a)
          })

          // Keep the first (highest priority) slot, delete the rest
          const slotsToDelete = sortedSlots.slice(1)
          const idsToDelete = slotsToDelete.map(slot => slot.id)

          if (idsToDelete.length > 0) {
            const { error: deleteError } = await supabase
              .from("slot")
              .delete()
              .in("id", idsToDelete)

            if (deleteError) {
              console.error(`Error deleting duplicate slots for ${timeKey}:`, deleteError)
            } else {
              console.log(`Deleted ${idsToDelete.length} duplicate slots for ${timeKey}`)
            }
          }
        }
      }

      return true
    } catch (error) {
      console.error("Error in cleanupDuplicateSlots:", error)
      return false
    }
  }

  // ENHANCED: Create slots for a specific date with duplicate prevention
  static async createSlotsForDate(date: string): Promise<boolean> {
    try {
      // First, cleanup any existing duplicates
      await this.cleanupDuplicateSlots(date)

      // Check if slots already exist after cleanup
      const { data: existingSlots, error: fetchError } = await supabase
        .from("slot")
        .select("time_start, time_end")
        .eq("date", date)

      if (fetchError) {
        console.error("Error checking existing slots:", fetchError)
        return false
      }

      const existingTimes = new Set(
        (existingSlots || []).map(slot => `${slot.time_start}-${slot.time_end}`)
      )

      const timeSlots = [
        { start: "06:00", end: "07:00" },
        { start: "07:00", end: "08:00" },
        { start: "08:00", end: "09:00" },
        { start: "09:00", end: "10:00" },
        { start: "10:00", end: "11:00" },
        { start: "11:00", end: "12:00" },
        { start: "12:00", end: "13:00" },
        { start: "13:00", end: "14:00" },
        { start: "14:00", end: "15:00" },
        { start: "15:00", end: "16:00" },
        { start: "16:00", end: "17:00" },
        { start: "17:00", end: "18:00" },
        { start: "18:00", end: "19:00" },
        { start: "19:00", end: "20:00" },
        { start: "20:00", end: "21:00" },
      ]

      // Only create slots that don't already exist
      const slotsToInsert = timeSlots
        .filter(slot => !existingTimes.has(`${slot.start}-${slot.end}`))
        .map(slot => ({
          date,
          time_start: slot.start,
          time_end: slot.end,
          status: "pending" as const,
          booked_by_user_id: null,
        }))

      if (slotsToInsert.length === 0) {
        console.log("All slots already exist for this date. No new slots created.")
        return true
      }

      const { error } = await supabase.from("slot").insert(slotsToInsert)

      if (error) {
        console.error("Error creating slots:", error)
        return false
      }

      console.log(`Created ${slotsToInsert.length} new slots for ${date}`)
      return true
    } catch (error) {
      console.error("Error in createSlotsForDate:", error)
      return false
    }
  }

  // Book slots for a user
  static async bookSlots(slotIds: string[], userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("slot")
        .update({
          booked_by_user_id: userId,
          status: "pending",
        })
        .in("id", slotIds)
        .is("booked_by_user_id", null) // Only update if not already booked
        .neq("status", "blocked") // Don't book blocked slots

      if (error) {
        console.error("Error booking slots:", error)
        return false
      }

      return true
    } catch (error) {
      console.error("Error in bookSlots:", error)
      return false
    }
  }

  // Block slots and cancel existing bookings with blocking reason
  static async blockSlots(slotIds: string[], reason: string): Promise<boolean> {
    try {
      // First, find and cancel existing bookings for these slots
      const { data: bookings } = await supabase
        .from('bookings')
        .select('id, slot_id')
        .in('slot_id', slotIds)
        .neq('status', 'rejected'); // Don't update already rejected bookings
      
      if (bookings && bookings.length > 0) {
        const bookingIds = bookings.map(b => b.id);
        
        // Update bookings to rejected status (using existing schema)
        const { error: bookingError } = await supabase
          .from('bookings')
          .update({ 
            status: 'rejected'
          })
          .in('id', bookingIds);
        
        if (bookingError) {
          console.error("Error cancelling bookings:", bookingError);
          return false;
        }
      }

      // Then block the slots
      const { error } = await supabase
        .from('slot')
        .update({
          status: 'blocked',
          booked_by_user_id: null,
          block_reason: reason
        })
        .in('id', slotIds);

      if (error) {
        console.error("Error blocking slots:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in blockSlots:', error);
      return false;
    }
  }

  // Unblock slots
  static async unblockSlots(slotIds: string[]): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('slot')
        .update({
          status: 'pending',
          booked_by_user_id: null,
          block_reason: null
        })
        .in('id', slotIds);

      if (error) {
        console.error("Error unblocking slots:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in unblockSlots:', error);
      return false;
    }
  }

  // Get all manually blocked slots (excluding admin bookings)
  static async getManuallyBlockedSlots(): Promise<ManualBlockingHistory[]> {
    try {
      const { data, error } = await supabase
        .from("slot")
        .select("id, date, time_start, time_end, status, block_reason, booked_by_user_id")
        .eq("status", "blocked")
        .neq("block_reason", "Admin Booking")
        .not("block_reason", "is", null)
        .order("date", { ascending: false })
        .order("time_start")

      if (error) {
        console.error("Error getting manually blocked slots:", error)
        return []
      }

      return data || []
    } catch (error) {
      console.error("Error in getManuallyBlockedSlots:", error)
      return []
    }
  }

  // Get manually blocked slots for a specific date
  static async getManuallyBlockedSlotsForDate(date: string): Promise<ManualBlockingHistory[]> {
    try {
      const { data, error } = await supabase
        .from("slot")
        .select("id, date, time_start, time_end, status, block_reason, booked_by_user_id")
        .eq("date", date)
        .eq("status", "blocked")
        .neq("block_reason", "Admin Booking")
        .not("block_reason", "is", null)
        .order("time_start")

      if (error) {
        console.error("Error getting manually blocked slots for date:", error)
        return []
      }

      return data || []
    } catch (error) {
      console.error("Error in getManuallyBlockedSlotsForDate:", error)
      return []
    }
  }

  // Get manual blocking statistics
  static async getManualBlockingStats(): Promise<{
    totalBlocked: number
    uniqueDates: number
    topReasons: Array<{ reason: string; count: number }>
  }> {
    try {
      const blockedSlots = await this.getManuallyBlockedSlots()
      
      const uniqueDates = new Set(blockedSlots.map(slot => slot.date)).size
      const reasonCounts = blockedSlots.reduce((acc, slot) => {
        const reason = slot.block_reason || 'No reason provided'
        acc[reason] = (acc[reason] || 0) + 1
        return acc
      }, {} as Record<string, number>)

      const topReasons = Object.entries(reasonCounts)
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)

      return {
        totalBlocked: blockedSlots.length,
        uniqueDates,
        topReasons
      }
    } catch (error) {
      console.error("Error getting manual blocking stats:", error)
      return {
        totalBlocked: 0,
        uniqueDates: 0,
        topReasons: []
      }
    }
  }

  // Get blocking reason for a slot
  static async getSlotBlockingReason(slotId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from("slot")
        .select("block_reason")
        .eq("id", slotId)
        .single()

      if (error) {
        console.error("Error getting slot blocking reason:", error)
        return null
      }

      return data?.block_reason || null
    } catch (error) {
      console.error("Error in getSlotBlockingReason:", error)
      return null
    }
  }

  // Get all admin booking slots (slots with "Admin Booking" block reason)
  static async getAdminBookingSlots(): Promise<ManualBlockingHistory[]> {
    try {
      const { data, error } = await supabase
        .from("slot")
        .select("id, date, time_start, time_end, status, block_reason, booked_by_user_id")
        .eq("block_reason", "Admin Booking")
        .order("date", { ascending: false })
        .order("time_start")

      if (error) {
        console.error("Error getting admin booking slots:", error)
        return []
      }

      return data || []
    } catch (error) {
      console.error("Error in getAdminBookingSlots:", error)
      return []
    }
  }

  // Cancel admin booking by removing block reason and making slots available
  static async cancelAdminBooking(slotIds: string[]): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('slot')
        .update({
          status: 'pending',
          booked_by_user_id: null,
          block_reason: null
        })
        .in('id', slotIds)
        .eq('block_reason', 'Admin Booking');

      if (error) {
        console.error("Error cancelling admin booking:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in cancelAdminBooking:', error);
      return false;
    }
  }

  // UTILITY: Clean up all duplicate slots across all dates (use with caution)
  static async cleanupAllDuplicateSlots(): Promise<boolean> {
    try {
      const { data: uniqueDates, error } = await supabase
        .from("slot")
        .select("date")
        .order("date")

      if (error) {
        console.error("Error getting unique dates:", error)
        return false
      }

      const dates = [...new Set((uniqueDates || []).map(slot => slot.date))]
      
      for (const date of dates) {
        await this.cleanupDuplicateSlots(date)
      }

      return true
    } catch (error) {
      console.error("Error in cleanupAllDuplicateSlots:", error)
      return false
    }
  }

  // Clean up orphaned slots (slots with booked_by_user_id but no active booking)
  static async cleanupOrphanedSlots(date?: string): Promise<number> {
    try {
      // Get slots with booked_by_user_id set
      let query = supabase
        .from("slot")
        .select("id, booked_by_user_id")
        .not("booked_by_user_id", "is", null)

      if (date) {
        query = query.eq("date", date)
      }

      const { data: slotsWithBooking, error: slotsError } = await query

      if (slotsError || !slotsWithBooking || slotsWithBooking.length === 0) {
        return 0
      }

      const slotIds = slotsWithBooking.map(slot => slot.id)

      // Get all active bookings for these slots
      const { data: activeBookings, error: bookingsError } = await supabase
        .from("bookings")
        .select("slot_id")
        .in("slot_id", slotIds)
        .in("status", ["pending", "approved", "booked", "paid"])

      if (bookingsError) {
        console.error("Error getting active bookings for cleanup:", bookingsError)
        return 0
      }

      // Find slots that have booked_by_user_id but no active booking
      const bookedSlotIds = new Set(
        (activeBookings || []).map(booking => booking.slot_id)
      )

      const orphanedSlotIds = slotIds.filter(slotId => !bookedSlotIds.has(slotId))

      if (orphanedSlotIds.length === 0) {
        return 0
      }

      // Clean up orphaned slots by clearing booked_by_user_id and setting status to pending
      const { error: updateError } = await supabase
        .from("slot")
        .update({
          booked_by_user_id: null,
          status: "pending",
          booking_id: null
        })
        .in("id", orphanedSlotIds)
        .neq("status", "blocked") // Don't clear blocked slots

      if (updateError) {
        console.error("Error cleaning up orphaned slots:", updateError)
        return 0
      }

      console.log(`Cleaned up ${orphanedSlotIds.length} orphaned slots`)
      return orphanedSlotIds.length
    } catch (error) {
      console.error("Error in cleanupOrphanedSlots:", error)
      return 0
    }
  }
}